import React from "react";
import { Button, Badge } from "react-bootstrap";
import { TABLE_ATTRIBUTE_TYPES } from "../../settings/constants/types";
import CollectionObjectDropdown from "../../components/tableMaster/tableView/CollectionObjectDropdown";
import DataTypeFactory from "./DataTypeFactory.jsx";

/**
 * ListComponent - A reusable component for handling list types in forms
 * 
 * This component automatically handles different data types in lists:
 * - OBJECT: Uses ObjectField for nested attributes (handles recursion internally)
 * - COLLECTION_OBJECT: Uses CollectionObjectDropdown for each item
 * - Other types: Uses DataTypeFactory for standard form fields
 * 
 * @param {Object} props
 * @param {Object} props.field - The field configuration object
 * @param {string} props.field.name - Field name
 * @param {string} props.field.type - Data type (OBJECT, COLLECTION_OBJECT, etc.)
 * @param {boolean} props.field.listAttribute - Whether this is a list type
 * @param {Object} props.field.config - Field configuration (attributes, enumValues, etc.)
 * @param {Array|Object} props.value - Current value of the list
 * @param {Function} props.onChange - Callback when list value changes
 * @param {number} props.level - Nesting level (for recursive rendering)
 * 
 * @example
 * // For OBJECT list
 * <ListComponent
 *   field={{ name: 'addresses', type: 'OBJECT', listAttribute: true }}
 *   value={[{ street: '123 Main St' }, { street: '456 Oak Ave' }]}
 *   onChange={(newList) => setAddresses(newList)}
 * />
 * 
 * @example
 * // For COLLECTION_OBJECT list
 * <ListComponent
 *   field={{ name: 'userIds', type: 'COLLECTION_OBJECT', listAttribute: true }}
 *   value={[{ id: '123', name: 'John' }, { id: '456', name: 'Jane' }]}
 *   onChange={(newList) => setUserIds(newList)}
 * />
 */
const ListComponent = ({ 
    field, 
    value, 
    onChange, 
    level = 0
}) => {
    const { name, type, listAttribute } = field;
    
    if (!listAttribute) {
        return null; // Not a list type
    }

    const handleAddItem = () => {
        const currentList = Array.isArray(value) ? value : [];
        let newItem;
        
        switch (type) {
            case TABLE_ATTRIBUTE_TYPES.OBJECT:
                newItem = {};
                break;
            case TABLE_ATTRIBUTE_TYPES.COLLECTION_OBJECT:
                newItem = { id: null, name: null };
                break;
            case TABLE_ATTRIBUTE_TYPES.ENUM:
                newItem = "";
                break;
            case TABLE_ATTRIBUTE_TYPES.STRING:
            case TABLE_ATTRIBUTE_TYPES.TEXT:
                newItem = "";
                break;
            case TABLE_ATTRIBUTE_TYPES.NUMBER:
            case TABLE_ATTRIBUTE_TYPES.INTEGER:
            case TABLE_ATTRIBUTE_TYPES.DECIMAL:
                newItem = 0;
                break;
            case TABLE_ATTRIBUTE_TYPES.BOOLEAN:
                newItem = false;
                break;
            case TABLE_ATTRIBUTE_TYPES.DATE:
            case TABLE_ATTRIBUTE_TYPES.DATETIME:
                newItem = "";
                break;
            default:
                newItem = "";
                break;
        }
        onChange([...currentList, newItem]);
    };

    const handleRemoveItem = (index) => {
        const newList = value.filter((_, i) => i !== index);
        onChange(newList);
    };

    const handleUpdateItem = (index, updatedItem) => {
        const newList = [...value];
        newList[index] = updatedItem;
        onChange(newList);
    };

    const renderListItem = (item, index) => {
        const itemField = {
            ...field,
            listAttribute: false, // Treat as single item
            name: `${field.name}_${index}`
        };

        return (
            <div key={index} className="mb-3 p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6>{type} {index + 1}</h6>
                    <Button 
                        variant="danger" 
                        size="sm"
                        onClick={() => handleRemoveItem(index)}
                    >
                        Remove
                    </Button>
                </div>
                
                {/* Delegate to appropriate component based on type */}
                {type === TABLE_ATTRIBUTE_TYPES.OBJECT && (
                    <DataTypeFactory
                        type={type}
                        value={item}
                        onChange={(updatedItem) => {
                            handleUpdateItem(index, updatedItem);
                        }}
                        mode="edit"
                        config={{
                            ...field.config,
                            attributes: field.config?.attributes || field.attributes || []
                        }}
                        field={itemField}
                    />
                )}
                
                {type === TABLE_ATTRIBUTE_TYPES.COLLECTION_OBJECT && (
                    <CollectionObjectDropdown
                        field={field}
                        name={itemField.name}
                        value={item}
                        onChange={(event) => {
                            const actualValue = event?.target?.value || event;
                            handleUpdateItem(index, actualValue);
                        }}
                    />
                )}
                
                {/* For other types, use DataTypeFactory */}
                {type !== TABLE_ATTRIBUTE_TYPES.OBJECT && type !== TABLE_ATTRIBUTE_TYPES.COLLECTION_OBJECT && (
                    <DataTypeFactory
                        type={type}
                        value={item}
                        onChange={(updatedItem) => {
                            handleUpdateItem(index, updatedItem);
                        }}
                        mode="edit"
                        config={{
                            ...field.config,
                            attributes: field.config?.attributes || field.attributes || []
                        }}
                        options={type === TABLE_ATTRIBUTE_TYPES.ENUM ? 
                            (field.config?.enumValues || field.enumValues || []).map(val => ({ label: val, value: val })) : 
                            undefined
                        }
                        field={itemField}
                    />
                )}
            </div>
        );
    };

    return (
        <div className="list-component">
            <div className="d-flex justify-content-between gap-1 align-items-center mb-3">
                <div><strong>{name}</strong> <Badge bg="info" className="ms-2">{type} List</Badge></div>
                <Button 
                    variant="primary" 
                    size="sm"
                    onClick={handleAddItem}
                    style={{
                        fontSize: "12px",
                        width: "100px"
                    }}
                >
                    + Add {type}
                </Button>
            </div>
            
            {Array.isArray(value) && value.map((item, index) => renderListItem(item, index))}
            
            {(!Array.isArray(value) || value.length === 0) && (
                <div className="text-muted">
                    <small>No {type} items added yet. Click "Add {type}" to create one.</small>
                </div>
            )}
        </div>
    );
};

export default ListComponent; 